import * as bytescale from "@bytescale/sdk";
import { safeParse } from "valibot";
import { getErrorMessage } from "./helpers/errors";
import { onUploadStatusChange } from "./helpers/events";
import { type UploadFileArgs, UploadFileArgsSchema } from "./schema";
import { getCanUpload, uploadAuthentication } from "./upload-authentication";

export let cancelledUploads: Array<string> = [];

export function cancelUpload(uploadId: string) {
  cancelledUploads.push(uploadId);
}

export function hasBeenCancelled(uploadId: string) {
  return cancelledUploads.includes(uploadId);
}

export async function uploadFile(args: UploadFileArgs) {
  const result = safeParse(UploadFileArgsSchema, args);

  if (!result.success) {
    throw new Error(JSON.stringify(result.issues));
  }

  const { file, folderPath, publicKey, uploadId } = result.output;
  const uploadManager = new bytescale.UploadManager({
    apiKey: publicKey,
  });

  const startTime = Date.now();
  onUploadStatusChange({ uploadId, file, status: "started", key: args.key });

  let canUpload = getCanUpload();

  if (!canUpload) {
    await uploadAuthentication({
      accountId: args.accountId,
      authUrl: "/auth/bytescale",
      canUpload: args.canUpload,
    });

    canUpload = getCanUpload();
  }

  if (!canUpload) {
    onUploadStatusChange({
      status: "error",
      key: args.key,
      reason: "Upload not allowed",
      uploadId,
      file,
    });
    throw new Error("User is not allowed to upload files");
  }

  const cancellationToken = { isCancelled: false };
  const cancellationInterval = setInterval(() => {
    if (hasBeenCancelled(uploadId)) {
      cancellationToken.isCancelled = true;
      clearInterval(cancellationInterval);
    }
  }, 200);

  try {
    const fileParts = file.name.split(".");
    const fileExtension = fileParts.pop();
    const bytescaleFileName = `${fileParts.join(".")}-${Date.now()}.${fileExtension}`;

    const response = await uploadManager.upload({
      data: file,
      onProgress: (status) => {
        if (cancelledUploads.includes(uploadId)) {
          return;
        }
        const rate = status.bytesSent / (Date.now() - startTime) / 1000;
        onUploadStatusChange({
          key: args.key,
          uploadId,
          file,
          progress: Number.isNaN(status.progress) ? 0 : status.progress,
          rate: Number.isNaN(rate) ? 0 : rate,
          status: "uploading",
        });
      },
      path: {
        folderPath,
        fileName: bytescaleFileName,
      },
      cancellationToken,
    });

    onUploadStatusChange({
      uploadId,
      file,
      status: "completed",
      key: args.key,
      response,
      bytescaleFileName,
    });

    return response;
  } catch (error) {
    if (!hasBeenCancelled(uploadId)) {
      onUploadStatusChange({
        key: args.key,
        uploadId,
        file,
        status: "error",
        reason: getErrorMessage(error),
      });
    }
    throw error;
  } finally {
    clearInterval(cancellationInterval);
    if (hasBeenCancelled(uploadId)) {
      cancelledUploads = cancelledUploads.filter(
        (cancelledUploadId) => cancelledUploadId !== uploadId,
      );
    }
  }
}
